import type { RefresherEventDetail } from '@ionic/core/components';
import {
  IonContent,
  IonPage,
  IonItem,
  IonToolbar,
  IonSkeletonText,
  IonRefresher,
  IonRefresherContent,
  IonRow,
} from '@ionic/react';
import { chevronDownCircleOutline } from 'ionicons/icons';
import React, { useState, useEffect, useRef, useContext } from 'react';
import { useTranslation } from 'react-i18next';
import { useHistory } from 'react-router';
import { Link } from 'react-router-dom';

import Clock from '../../assets/Clock.svg';
import CreateAsset from '../../assets/CreateAsset.svg';
import axios from '../../axios';
import Header from '../../components/Header';
import NoRecords from '../../components/NoRecords';
import { AppContext } from '../../providers/AppContext';

const SurveyHome: React.FC = () => {
  const { state } = useContext(AppContext);

  const isMounted = useRef(true);
  const [data, setData] = useState<any>({
    dataSet: [],
  });
  const [isLoading, setIsLoading] = useState(true);
  const { t } = useTranslation();
  const history = useHistory();

  const getData = () => {
    isMounted.current = true; // Set isMounted to true
    const url = '/resource/Survey?limit_page_length=5&limit_start=0&fields=["*"]';

    const config: any = {
      method: 'get',
      url,
      headers: {
        Authorization: `Basic ${localStorage.getItem('hyprism_login')}`,
      },
    };
    axios(config)
      .then((res) => {
        const Data = { ...data };
        Data.dataSet = res.data.data;
        if (isMounted.current)
          setData(Data);
      })
      .catch((err) => {
        console.log(err);
      })
      .finally(() => {
        if (isMounted.current)
          setIsLoading(false);
      });
  };
  function doRefresh(event: CustomEvent<RefresherEventDetail>) {
    getData();
    setTimeout(() => {
      console.log('Async operation has ended');
      event.detail.complete();
    }, 2000);
  }

  useEffect(() => {

    getData();

    // Cleanup function
    return () => {
      isMounted.current = false; // Mark the component as unmounted when it's being cleaned up
      // You can also cancel any subscriptions or clean up other resources here
    };
  }, [history.location.state]);

  return (
    <IonPage>
      <Header enableBackButton="" title="" />
      <IonContent>
        <IonRefresher slot="fixed" onIonRefresh={doRefresh}>
          <IonRefresherContent
            pullingIcon={chevronDownCircleOutline}
            pullingText={t('Utils.pullRefresh')}
            refreshingSpinner="circles"
            refreshingText={t('Utils.refreshing')}
          />
        </IonRefresher>

        {/* ── Hero Banner ── */}
        <div style={{
          background: 'linear-gradient(135deg, #1d508d 0%, #0f3460 100%)',
          padding: '16px 16px 16px',
          position: 'relative',
          overflow: 'hidden',
        }}>
          <div style={{ position: 'absolute', top: -30, right: -30, width: 120, height: 120, borderRadius: '50%', background: 'rgba(255,255,255,0.06)' }} />
          <div style={{ position: 'absolute', bottom: -20, left: -20, width: 80, height: 80, borderRadius: '50%', background: 'rgba(255,255,255,0.04)' }} />

          <p style={{ margin: 0, fontSize: 11, fontWeight: 600, color: 'rgba(255,255,255,0.6)', letterSpacing: '0.12em', textTransform: 'uppercase', position: 'relative', zIndex: 1, marginBottom: '1rem' }}>
            {t('Survey.recent')}
          </p>
          {/* <h1 style={{ margin: '3px 0 12px', fontSize: 22, fontWeight: 800, color: '#fff', lineHeight: 1.15, position: 'relative', zIndex: 1 }}>
            Survey
          </h1> */}

          {/* Quick action buttons inside banner */}
          <div style={{ display: 'flex', gap: 8, overflowX: 'auto', paddingBottom: 4, position: 'relative', zIndex: 1 }}>
            {!!state.permissions.survey_create && (
              <Link to="/create-survey" style={{ textDecoration: 'none', flexShrink: 0 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 6, background: '#fff', borderRadius: 14, padding: '10px 16px', boxShadow: '0 4px 16px rgba(29,80,141,0.15)', border: '1.5px solid rgba(29,80,141,0.1)', cursor: 'pointer' }}>
                  <img src={CreateAsset} alt="create" style={{ width: 18, height: 18 }} />
                  <span style={{ fontSize: 13, fontWeight: 700, color: '#1d508d', whiteSpace: 'nowrap' }}>{t('Survey.create')}</span>
                </div>
              </Link>
            )}
            <Link to="/show-survey" style={{ textDecoration: 'none', flexShrink: 0 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 6, background: 'rgba(255,255,255,0.15)', borderRadius: 14, padding: '10px 16px', border: '1.5px solid rgba(255,255,255,0.3)', cursor: 'pointer' }}>
                <img src={Clock} alt="history" style={{ width: 16, height: 16, filter: 'brightness(10)' }} />
                <span style={{ fontSize: 13, fontWeight: 700, color: '#fff', whiteSpace: 'nowrap' }}>{t('Survey.history')}</span>
              </div>
            </Link>
          </div>
        </div>

        {/* ── Section Header ── */}
        <div style={{ padding: '16px 16px 4px' }}>
          <p style={{ margin: 0, fontSize: 15, fontWeight: 800, color: '#1d508d' }}>{t('Survey.recent')}</p>
        </div>

        {/* ── Skeleton Loaders ── */}
        {isLoading && [1, 2, 3, 4, 5].map((key) => (
          <div key={key} style={{ margin: '0 12px 10px', background: '#fff', borderRadius: 16, padding: '16px', boxShadow: '0 2px 12px rgba(0,0,0,0.06)' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 8 }}>
              <IonSkeletonText animated style={{ width: '55%', height: 16, borderRadius: 8 }} />
              <IonSkeletonText animated style={{ width: '20%', height: 16, borderRadius: 8 }} />
            </div>
            <IonSkeletonText animated style={{ width: '40%', height: 12, borderRadius: 8 }} />
          </div>
        ))}

        {/* ── Survey Cards ── */}
        <div style={{ padding: '0 12px' }}>
          {data.dataSet.map((item: any, key: any) => (
            <div key={key} style={{
              background: '#fff',
              borderRadius: 18,
              padding: '14px 16px',
              marginBottom: 10,
              boxShadow: '0 3px 14px rgba(0,0,0,0.07)',
              border: '1.5px solid rgba(29,80,141,0.07)',
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'flex-start',
              gap: 12,
            }}>
              <div style={{ flex: 1, minWidth: 0 }}>
                <p style={{ margin: '0 0 4px', fontSize: 15, fontWeight: 700, color: '#1d2d44', wordWrap: 'break-word' }}>
                  {item.name}
                </p>
                <p style={{ margin: 0, fontSize: 12, color: '#6b7b8f' }}>
                  {t('Survey.code')}: <span style={{ fontWeight: 600, color: '#1d508d' }}>{item.si_survey_code}</span>
                </p>
              </div>

              <div style={{ textAlign: 'right', flexShrink: 0 }}>
                <Link
                  to={{ pathname: '/view-survey', state: item.name, search: 'back=survey-home' }}
                  style={{
                    display: 'inline-block',
                    background: 'rgba(29,80,141,0.08)',
                    color: '#1d508d',
                    padding: '4px 12px',
                    borderRadius: 14,
                    fontSize: 12,
                    fontWeight: 700,
                    textDecoration: 'none',
                    marginBottom: 6,
                  }}
                >
                  {t('Assets.Details.Details')}
                </Link>
                {item.men_status && (
                  <p style={{ margin: 0, color: '#929bab', fontSize: 11, fontWeight: 600 }}>
                    {item.men_status}
                  </p>
                )}
              </div>
            </div>
          ))}

          {data && data.dataSet.length > 0 && (
            <Link
              to="/show-survey"
              style={{ display: 'block', textAlign: 'center', margin: '4px 0 24px', fontSize: 13, fontWeight: 600, color: '#1d508d', textDecoration: 'none', borderBottom: '1.5px solid rgba(29,80,141,0.3)', width: 'fit-content', marginInline: 'auto' }}
            >
              {t('Utils.viewAll')}
            </Link>
          )}
        </div>

        {/* ── Empty State ── */}
        {!isLoading && data.dataSet.length === 0 && (
          <IonRow>
            <NoRecords width="30%" smallText={true} />
          </IonRow>
        )}
      </IonContent>
    </IonPage>
  );
};

export default SurveyHome;
// withRouter();
